<template>
  <form @submit="onSubmit">
    <div class="flex flex-col lg:flex-row gap-x-4 gap-y-2 p-2">
      <div class="flex flex-col gap-2 w-full lg:w-1/2">
        <StackedLabelInput name="companyName" label="Company Name" />
        <StackedLabelInput name="firstName" label="First Name" />
        <StackedLabelInput name="lastName" label="Last Name" />
      </div>
      <div class="flex flex-col gap-2 w-full lg:w-1/2">
        <StackedLabelInput name="fedTaxId" label="Federal Tax ID" />
        <StackedLabelInput name="stateTaxId" label="State Tax ID" />
      </div>
    </div>
    <slot name="navigation"> </slot>
  </form>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { type Entity } from "@/types/Entity";
import { z } from "zod";

const emit = defineEmits<{
  submitStep: [values: any];
}>();

interface Props {
  entity?: Entity;
  data: any;
  setValues?: boolean;
  mode: "Add" | "Edit" | "Copy";
}
const props = withDefaults(defineProps<Props>(), {
  setValues: false,
});

const initialValues: any = {};

if (props.entity) {
  if (props.mode === "Edit") {
    initialValues.firstName = props.entity.firstName;
    initialValues.lastName = props.entity.lastName;
    initialValues.companyName = props.entity.companyName;
    initialValues.fedTaxId = props.entity.fedTaxId;
    initialValues.stateTaxId = props.entity.stateTaxId;
  } else {
    initialValues.firstName = "";
    initialValues.lastName = "";
    initialValues.companyName = "";
    initialValues.fedTaxId = "";
    initialValues.stateTaxId = "";
  }
}

const { handleSubmit, setFieldValue } = useForm({
  validationSchema: toTypedSchema(
    z
      .object({
        companyName: z.string().default(""),
        firstName: z.string().default(""),
        lastName: z.string().default(""),
        fedTaxId: z.string().default(""),
        stateTaxId: z.string().default(""),
      })
      .refine((data) => data.companyName.length > 0 || (data.firstName.length > 0 && data.lastName.length > 0), {
        message: "Either a company name or first and last name is required",
        path: ["companyName", "firstName", "lastName"],
      })
  ),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

if (props.setValues) {
  setFieldValue("companyName", props.data.companyName);
  setFieldValue("firstName", props.data.firstName);
  setFieldValue("lastName", props.data.lastName);
  setFieldValue("fedTaxId", props.data.fedTaxId);
  setFieldValue("stateTaxId", props.data.stateTaxId);
}

const onSubmit = handleSubmit((values: any): void => {
  emit("submitStep", values);
});
</script>
